/**
 * WSIE Global Layer — renders all active sticky notes + click-to-stamp mode.
 *
 * Sticky Mode (press S or click the stamp button):
 *  • Cursor shows a custom sticky-note crosshair
 *  • Left-click ANYWHERE stamps a note at that exact position
 *  • An inline quick-entry textarea opens immediately on the note
 *  • Enter / click-away = save  •  Escape = discard
 *  • Right-click = exit sticky mode
 *
 * Full-form create still available via the FAB long-press / manager.
 */
import { useEffect, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { StickyNote as StickyIcon, X, ChevronDown } from 'lucide-react';
import {
  useWSIEStore, makeStickyNote,
  NOTE_TYPE_META, NOTE_COLORS,
  type NoteType, type NoteColor, type NoteStyle,
} from '../../stores/wsie.store.js';
import { useAuthStore } from '../../stores/auth.store.js';
import { WSIEStickyNote } from './WSIEStickyNote.js';
import { WSIECreatePanel } from './WSIECreatePanel.js';

// ── Cursor ghost that follows mouse in sticky mode ─────────────────────────────
function StickyGhost({ color }: { color: NoteColor }) {
  const ref = useRef<HTMLDivElement>(null);
  const c = NOTE_COLORS[color];

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const move = (e: MouseEvent) => {
      el.style.left = `${e.clientX + 8}px`;
      el.style.top  = `${e.clientY + 8}px`;
    };
    window.addEventListener('mousemove', move, { passive: true });
    return () => window.removeEventListener('mousemove', move);
  }, []);

  return (
    <div ref={ref} style={{
      position: 'fixed', zIndex: 20100, pointerEvents: 'none',
      width: 52, height: 46,
      background: c.bg,
      border: `2px solid ${c.accent}`,
      borderRadius: 4,
      opacity: 0.85,
      boxShadow: `0 4px 12px ${c.shadow}`,
      transform: 'rotate(-4deg)',
      transition: 'left .04s linear, top .04s linear',
    }}>
      {/* tape strip at top */}
      <div style={{ position: 'absolute', top: -7, left: '50%', transform: 'translateX(-50%)', width: 22, height: 12, background: 'rgba(173,210,230,0.7)', borderRadius: 2, border: '1px solid rgba(173,210,230,0.95)' }} />
      <div style={{ position: 'absolute', inset: '10px 5px 4px', display: 'flex', flexDirection: 'column', gap: 3 }}>
        {[1, 2, 3].map((i) => (
          <div key={i} style={{ height: 2, background: `${c.accent}55`, borderRadius: 1 }} />
        ))}
      </div>
      {/* crosshair dot */}
      <div style={{ position: 'absolute', bottom: -4, right: -4, width: 10, height: 10, borderRadius: '50%', background: c.accent, border: '2px solid white' }} />
    </div>
  );
}

// ── Quick inline editor that appears on a freshly stamped note ─────────────────
function QuickNoteEditor({ noteId, color }: { noteId: string; color: NoteColor }) {
  const { updateNote, deleteNote, setQuickEditId, setStickyMode } = useWSIEStore();
  const user = useAuthStore((s) => s.user?.email ?? 'user');
  const [text, setText] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { ref.current?.focus(); }, []);
  const c = NOTE_COLORS[color];

  const save = useCallback(() => {
    if (!text.trim()) {
      deleteNote(noteId, user);
    } else {
      updateNote(noteId, { content: text.trim() });
    }
    setQuickEditId(null);
  }, [text, noteId, user, updateNote, deleteNote, setQuickEditId]);

  const discard = useCallback(() => {
    deleteNote(noteId, user);
    setQuickEditId(null);
  }, [noteId, user, deleteNote, setQuickEditId]);

  return (
    <div style={{
      position: 'absolute', inset: '28px 0 0 0',
      display: 'flex', flexDirection: 'column', padding: '4px 6px', gap: 4,
    }}>
      <textarea
        ref={ref}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Type note... Enter to save"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save(); }
          if (e.key === 'Escape') discard();
        }}
        onBlur={save}
        style={{
          flex: 1, resize: 'none', background: 'transparent', border: 'none', outline: 'none',
          fontSize: 11, color: c.text, fontFamily: 'inherit', lineHeight: 1.5,
          padding: 0,
        }}
      />
      <div style={{ fontSize: 8, color: `${c.text}55`, textAlign: 'right' }}>Enter=save · Esc=discard</div>
    </div>
  );
}

// ── Type + color picker pill bar (visible in sticky mode) ─────────────────────
function StickyModeBar() {
  const { stickyType, stickyColor, setStickyMode, stickyMode } = useWSIEStore();
  const [open, setOpen] = useState(false);

  const types  = Object.entries(NOTE_TYPE_META) as [NoteType, typeof NOTE_TYPE_META[NoteType]][];
  const colors = Object.keys(NOTE_COLORS) as NoteColor[];
  const meta   = NOTE_TYPE_META[stickyType];
  const c      = NOTE_COLORS[stickyColor];

  if (!stickyMode) return null;

  return (
    <div style={{
      position: 'fixed', top: 64, left: '50%', transform: 'translateX(-50%)',
      zIndex: 20050, display: 'flex', alignItems: 'center', gap: 6,
      background: 'rgba(6,10,20,0.97)', border: `1px solid ${c.accent}66`,
      borderRadius: 12, padding: '6px 12px', backdropFilter: 'blur(16px)',
      boxShadow: `0 4px 20px rgba(0,0,0,0.6), 0 0 0 1px ${c.accent}22`,
    }}>
      {/* Mode indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingRight: 8, borderRight: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ width: 8, height: 8, borderRadius: 2, background: c.bg, border: `1px solid ${c.accent}` }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: c.accent, letterSpacing: '0.06em' }}>STICKY MODE</span>
        <span style={{ fontSize: 10, color: 'rgba(148,163,184,0.5)' }}>Left-click anywhere to stamp</span>
      </div>

      {/* Type selector */}
      <div style={{ position: 'relative' }}>
        <button onClick={() => setOpen((v) => !v)}
          style={{ height: 28, padding: '0 10px', borderRadius: 7, border: `1px solid ${c.accent}44`, background: `${c.accent}12`, color: c.accent, fontSize: 11, display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
          <span>{meta.icon}</span> {meta.label} <ChevronDown className="h-2.5 w-2.5" />
        </button>
        {open && (
          <div style={{ position: 'absolute', top: 32, left: 0, background: 'rgba(6,10,20,0.98)', border: '1px solid rgba(37,181,168,0.2)', borderRadius: 8, padding: 4, zIndex: 20060, minWidth: 170 }}>
            {types.map(([t, m]) => (
              <button key={t}
                onClick={() => { setStickyMode(true, t, m.color); setOpen(false); }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 7, padding: '5px 8px', borderRadius: 5, background: stickyType === t ? 'rgba(94,234,212,0.08)' : 'transparent', border: 'none', color: stickyType === t ? '#5eead4' : 'rgba(148,163,184,0.8)', fontSize: 11, cursor: 'pointer', textAlign: 'left' }}>
                <span>{m.icon}</span> {m.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Color dots */}
      <div style={{ display: 'flex', gap: 4, paddingLeft: 4 }}>
        {colors.map((col) => (
          <button key={col} onClick={() => setStickyMode(true, stickyType, col)}
            title={col}
            style={{ width: 16, height: 16, borderRadius: 4, background: NOTE_COLORS[col].bg, border: stickyColor === col ? `2px solid ${NOTE_COLORS[col].accent}` : `1px solid ${NOTE_COLORS[col].border}66`, cursor: 'pointer', flexShrink: 0 }} />
        ))}
      </div>

      {/* ESC to exit */}
      <button onClick={() => setStickyMode(false)}
        style={{ height: 26, width: 26, borderRadius: 6, border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)', color: 'rgba(248,113,113,0.8)', display: 'grid', placeItems: 'center', cursor: 'pointer', marginLeft: 4 }}>
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

// ── Global click handler ───────────────────────────────────────────────────────
function StickyClickCatcher() {
  const { stickyMode, stickyType, stickyColor, addNote, setQuickEditId, setStickyMode, quickEditId } = useWSIEStore();
  const user = useAuthStore((s) => s.user?.email ?? 'user');

  const stamp = useCallback((e: MouseEvent) => {
    if (!stickyMode) return;
    // Ignore clicks on existing notes, UI panels, inputs, etc.
    const target = e.target as HTMLElement;
    if (target.closest('[data-wsie-note]') || target.closest('[data-wsie-ui]') || target.closest('button') || target.closest('input') || target.closest('textarea') || target.closest('select')) return;

    e.preventDefault();
    e.stopPropagation();

    const px = (e.clientX / window.innerWidth)  * 100;
    const py = (e.clientY / window.innerHeight) * 100;

    const note = makeStickyNote({
      type:        stickyType,
      color:       stickyColor,
      content:     '',           // empty — inline editor fills it
      creator:     user,
      posX:        Math.min(px, 82),
      posY:        Math.min(py, 82),
      anchorRoute: window.location.hash.replace('#', '') || 'workspace',
      anchorLabel: document.title || 'Workspace',
    });

    addNote(note);
    setQuickEditId(note.id);
    // Hold sticky mode so user can stamp multiple notes
  }, [stickyMode, stickyType, stickyColor, user, addNote, setQuickEditId]);

  const exitOnRight = useCallback((e: MouseEvent) => {
    if (stickyMode && e.button === 2) { e.preventDefault(); setStickyMode(false); }
  }, [stickyMode, setStickyMode]);

  const exitOnEsc = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && stickyMode) setStickyMode(false);
    if ((e.key === 's' || e.key === 'S') && !stickyMode &&
        !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement)) {
      setStickyMode(true);
    }
  }, [stickyMode, setStickyMode]);

  useEffect(() => {
    if (!stickyMode) return;
    window.addEventListener('click', stamp, true);
    window.addEventListener('mousedown', exitOnRight);
    return () => {
      window.removeEventListener('click', stamp, true);
      window.removeEventListener('mousedown', exitOnRight);
    };
  }, [stickyMode, stamp, exitOnRight]);

  useEffect(() => {
    window.addEventListener('keydown', exitOnEsc);
    return () => window.removeEventListener('keydown', exitOnEsc);
  }, [exitOnEsc]);

  return null;
}

// ── Main WSIE global layer ─────────────────────────────────────────────────────
export function WSIEGlobalLayer() {
  const user = useAuthStore((s) => s.user?.email ?? '');
  const { notes, creating, openCreate, closeCreate, stickyMode, stickyColor, quickEditId, setStickyMode } = useWSIEStore();

  const activeNotes = notes.filter((n) => {
    if (n.status !== 'active') return false;
    if (user && n.hiddenFor.includes(user)) return false;
    const snooze = n.snoozedUntil[user];
    if (snooze && snooze > new Date().toISOString()) return false;
    return true;
  });

  if (!user) return null;

  return createPortal(
    <>
      {/* Sticky mode cursor ghost */}
      {stickyMode && <StickyGhost color={stickyColor} />}

      {/* Sticky mode toolbar */}
      <StickyModeBar />

      {/* Global click catcher (listens when sticky mode is on) */}
      <StickyClickCatcher />

      {/* Body cursor style */}
      {stickyMode && (
        <style>{`body { cursor: none !important; }`}</style>
      )}

      {/* All active sticky notes */}
      {activeNotes.map((note) => (
        <div key={note.id} data-wsie-note="1" style={{ position: 'absolute', top: 0, left: 0, width: 0, height: 0, overflow: 'visible' }}>
          <WSIEStickyNote
            note={note}
          />
        </div>
      ))}

      {/* FAB: toggle sticky mode OR open full form */}
      <div data-wsie-ui="1" style={{
        position: 'fixed', bottom: 100, right: 16, zIndex: 9200,
        display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6,
      }}>
        {/* Stamp toggle */}
        <button
          data-testid="wsie-stamp-btn"
          onClick={() => setStickyMode(!stickyMode)}
          title={stickyMode ? 'Exit Sticky Mode (ESC)' : 'Sticky Mode — click anywhere to stamp note (S)'}
          style={{
            width: 40, height: 40, borderRadius: 12,
            background: stickyMode ? NOTE_COLORS[stickyColor].bg : 'rgba(6,10,20,0.95)',
            border: stickyMode ? `2px solid ${NOTE_COLORS[stickyColor].accent}` : '1px solid rgba(251,191,36,0.5)',
            color: stickyMode ? NOTE_COLORS[stickyColor].accent : '#fbbf24',
            display: 'grid', placeItems: 'center', cursor: 'pointer',
            boxShadow: stickyMode ? `0 4px 20px ${NOTE_COLORS[stickyColor].shadow}` : '0 4px 16px rgba(251,191,36,0.2)',
            transition: 'all .2s',
            transform: stickyMode ? 'rotate(-6deg) scale(1.08)' : 'none',
          }}
        >
          <StickyIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Full-form create panel */}
      {creating && <WSIECreatePanel onClose={closeCreate} />}
    </>,
    document.body,
  );
}
